/**
 * UC1 constants shared between `Uc1.tsx`, its panels and its tests — split out of the
 * component modules so each of those stays a components-only file for React Fast Refresh.
 *
 * Every palette here is fixed by the client's launch spec
 * (`docs/client-specs/2026-09-29-UC1_LAUNCH_SPEC.md` §3, design §3): one colour family per
 * constraint, shared by the chip, the map and the scorecard, so a reader who has learned
 * "purple is agricultural area" never has to relearn it in another block.
 */

/** The three priority classes: High Priority, Moderate, Low. */
export const CATEGORY_COLORS: Record<string, string> = {
  high: '#b30000',
  moderate: '#e6a100',
  low: '#2a9d4a',
};

/** The three classes in map/legend order, darkest first. */
export const UC1_CATEGORIES = ['high', 'moderate', 'low'] as const;

/** One colour family per constraint — chip, map and scorecard read the same entry. */
export const CONSTRAINT_COLORS: Record<string, string> = {
  ag_area: '#6a51a3',
  productivity: '#c51b7d',
  soil: '#8c510a',
  climate: '#2166ac',
  access: '#01665e',
};

/**
 * "Not flagged", and the ground a not-assessed hatch is drawn over.
 *
 * Client round 3 Oct 2026: a clearly visible mid grey. The old `#d9ded9` was so close to
 * the panel ground that a reader could not tell an unflagged watershed from a gap in the
 * map, which made "not flagged" look like "nothing here" on every constraint chip.
 */
export const NEUTRAL_COLOR = '#9aa5a0';

/**
 * "Not assessed" as a **bar segment** (`#uc1ChipOverview`'s stacked bar). The map says this
 * with a hatch, which a stacked bar segment cannot carry, so the third class is a darker
 * neutral than "not flagged" — distinct from it without reading as a fourth colour family.
 */
export const NOT_ASSESSED_COLOR = '#6f7b76';

/**
 * Priority order, darkest class first — the sort key `#uc1UnitsChart`'s matrix puts its
 * rows in (priority, then name), so the places that need reading first are at the top.
 */
export const CATEGORY_RANK: Record<string, number> = { high: 0, moderate: 1, low: 2 };

/**
 * The client's launch decision: UC1 reads 2025 and nothing else (spec header, "reference
 * year 2025 only"). It is the year of the *inputs*, not of the run, so the Overview tile
 * prints this and the run stamp stays in Methodology's "Last computed".
 */
export const REFERENCE_YEAR = 2025;

/**
 * Amendment 1 §0: two unit levels anywhere in this feature — watershed and country
 * (`admin0`). Nothing commune-level (`admin2`) is computed, stored or shown any more.
 */
export const UC1_LEVELS = ['watershed', 'admin0'] as const;
export type Uc1Level = (typeof UC1_LEVELS)[number];

/**
 * The launch spec's five constraints, in scorecard order (C1…C5) — read by the chips, the
 * scorecard rows, "why" and the methodology thresholds table.
 */
export const UC1_DIMENSIONS = [
  'ag_area',
  'productivity',
  'soil',
  'climate',
  'access',
] as const;
export type Uc1Dimension = (typeof UC1_DIMENSIONS)[number];

/** `#uc1Chip-*`: Priority plus one chip per constraint, exclusive, Priority by default. */
export const UC1_CHIPS = ['priority', ...UC1_DIMENSIONS] as const;

/**
 * The stored chip, or `'priority'` when the store holds an id this build does not know (a
 * permalink or a session from before a chip was renamed). One normalisation, applied by
 * both the chips and the paint, so the pressed chip and the painted map can never disagree.
 */
export function normaliseChip(chip: string): string {
  return UC1_CHIPS.includes(chip as (typeof UC1_CHIPS)[number]) ? chip : 'priority';
}

/**
 * Which tercile class each constraint's rule looks for.
 *
 * The engine's `rule_hit` names it (`rain_2025:tercile_low`) only for a unit that actually
 * flagged — an unflagged row has no rule that fired, and the scorecard still has to say
 * which end of the distribution was being looked at. Every UC1 rule is fixed by the
 * definition, so the direction is a constant here and `rule_hit` only ever confirms it.
 */
export const UC1_TERCILE_CLASS: Record<string, 'low' | 'high'> = {
  ag_area: 'high',
  productivity: 'low',
  soil: 'low',
  climate: 'low',
  access: 'low',
};

/** A country roll-up's three share classes: under a third, up to two thirds, above. */
export const SHARE_BREAKS = [1 / 3, 2 / 3] as const;

/**
 * Which of the three roll-up steps a share falls in. `null` (no watershed of that country
 * could be assessed for the constraint) is not a step at all — the caller hatches it.
 */
export function shareStep(share: number): 0 | 1 | 2 {
  if (share < SHARE_BREAKS[0]) return 0;
  if (share < SHARE_BREAKS[1]) return 1;
  return 2;
}

/** `#rrggbb` → `[r, g, b]`. */
function rgbOf(hex: string): [number, number, number] {
  const n = parseInt(hex.slice(1), 16);
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
}

/**
 * `color` mixed `amount` of the way towards white — the country choropleth's "three
 * light-to-dark steps of the same colour" (design §3), computed rather than hand-picked so
 * the five families can never drift out of step with `CONSTRAINT_COLORS`.
 */
export function lighten(color: string, amount: number): string {
  const [r, g, b] = rgbOf(color);
  const mix = (c: number) => Math.round(c + (255 - c) * amount);
  return `#${[mix(r), mix(g), mix(b)].map((c) => c.toString(16).padStart(2, '0')).join('')}`;
}

/** The three steps of one constraint's family, lightest (fewest flagged) first. */
export function constraintSteps(dimension: string): [string, string, string] {
  const base = CONSTRAINT_COLORS[dimension] ?? NEUTRAL_COLOR;
  return [lighten(base, 0.62), lighten(base, 0.31), base];
}

/**
 * A small, non-alarming palette for the main-crops chart. Crop identity carries no category
 * meaning the way `CATEGORY_COLORS` does, so any fixed set reads fine; cycles if there are
 * more crops than colours.
 */
export const CROP_COLORS = ['#2f6d4f', '#6b9080', '#a4c3b2', '#cce3de', '#8B958A'];

/**
 * `values.context`'s own unit per id — "Context (not scored)" (`Uc1.tsx`'s Methodology
 * sub-tab) reads whichever of these keys the run happens to serve; a value without one here
 * (a future context id not listed yet) falls back to no unit rather than throwing.
 */
export const CONTEXT_UNITS: Record<string, string> = {
  wp_density: 'per 100 km²',
  mb_tenure_own: '%',
  gaez_gapsh_main: '%',
  gaez_yield_main: 't/ha',
};
